import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Plus, Trash2 } from 'lucide-react';
import { api } from '../lib/api';
import { useList } from '../hooks/useList';
import { DataTable, type Column } from '../components/ui/DataTable';
import { PageHeader, Badge, Select, Money, DateText, Button, Modal, Field, Input, Textarea, KeyValue, Card } from '../components/ui';
import { Can } from '../components/Can';

type N = Record<string, any>;
type Line = { description: string; unitPrice: string; taxCode: string };
const today = () => new Date().toISOString().slice(0, 10);
const blankLine = (): Line => ({ description: '', unitPrice: '', taxCode: '' });

const REASONS: Record<string, string[]> = {
  'AR:CREDIT': ['GOODWILL', 'CORRECTION', 'RETURN', 'BAD_DEBT', 'PRICE_ADJUSTMENT', 'OTHER'],
  'AR:DEBIT': ['ADDITIONAL_CHARGE', 'LATE_FEE', 'CORRECTION', 'REINSTATE_CREDIT_NOTE', 'OTHER'],
  'AP:CREDIT': ['PRICE_ADJUSTMENT', 'RETURN', 'CORRECTION', 'OTHER'],
  'AP:DEBIT': ['CONTRA_CHARGE', 'PRICE_ADJUSTMENT', 'CORRECTION', 'OTHER'],
};

/** What each of the four documents actually does, in the words a finance user would use. */
const EXPLAINER: Record<string, string> = {
  'AR:CREDIT': 'Reduces what a tenant owes. Applied to the invoice it was raised against.',
  'AR:DEBIT': 'Charges a tenant MORE against an issued invoice — an undercharge, a late fee, or reinstating a credit note raised in error. It becomes a receivable in its own right and is settled by a payment.',
  'AP:CREDIT': 'A supplier credits us. Reduces what we owe them.',
  'AP:DEBIT': 'We bill a supplier back — rework, damages, a contra charge. Also reduces what we owe them.',
};

/**
 * Credit notes and debit notes, sales and purchase side.
 *
 * An issued tax invoice is never edited: UK and UAE rules both require the original
 * to stand and any change to be made by a further document. This is that document —
 * and the debit note is the half most systems leave out.
 */
export default function NotesPage() {
  const qc = useQueryClient();
  const list = useList<N>('/notes', {});
  const [open, setOpen] = useState<N | null>(null);
  const [creating, setCreating] = useState(false);
  const [voiding, setVoiding] = useState<N | null>(null);
  const [reinstating, setReinstating] = useState<N | null>(null);
  const [reason, setReason] = useState('');
  const [form, setForm] = useState({ noteType: 'CREDIT', direction: 'AR', reasonCode: 'GOODWILL', reason: '', invoiceId: '', billId: '', issueDate: today(), notes: '', copyWholeDocument: true });
  const [lines, setLines] = useState<Line[]>([blankLine()]);

  const kind = `${form.direction}:${form.noteType}`;
  const invoices = useQuery({ queryKey: ['notes-invoice-picker'], queryFn: async () => (await api.get<N[]>('/invoices', { outstandingOnly: false, perPage: 100 })).data, enabled: creating && form.direction === 'AR' });
  const bills = useQuery({ queryKey: ['notes-bill-picker'], queryFn: async () => (await api.get<N[]>('/bills', { perPage: 100 })).data, enabled: creating && form.direction === 'AP' });
  const taxCodes = useQuery({ queryKey: ['tax-codes'], queryFn: async () => (await api.get<N>('/accounts/tax-codes')).data });
  const detail = useQuery({ queryKey: ['note', open?.id], queryFn: async () => (await api.get<N>(`/notes/${open!.id}`)).data, enabled: !!open });

  const refresh = () => { void qc.invalidateQueries({ queryKey: ['/notes'] }); if (open) void qc.invalidateQueries({ queryKey: ['note', open.id] }); };

  const create = useMutation({
    mutationFn: async () => (await api.post<N>('/notes', {
      noteType: form.noteType, direction: form.direction, reasonCode: form.reasonCode,
      reason: form.reason || undefined, issueDate: form.issueDate, notes: form.notes || undefined,
      invoiceId: form.direction === 'AR' ? form.invoiceId || undefined : undefined,
      billId: form.direction === 'AP' ? form.billId || undefined : undefined,
      // With a document named and "credit the whole thing" ticked, the lines are
      // copied server-side at the ORIGINAL tax codes — the safe path.
      lines: form.copyWholeDocument && (form.invoiceId || form.billId) ? undefined
        : lines.filter((l) => l.description && l.unitPrice).map((l) => ({ description: l.description, unitPriceMinor: Math.round(Number(l.unitPrice) * 100), taxCode: l.taxCode || undefined })),
    })).data,
    onSuccess: (n) => { setCreating(false); setLines([blankLine()]); refresh(); setOpen(n); },
  });
  const issue = useMutation({ mutationFn: async (id: string) => api.post(`/notes/${id}/issue`, { autoApply: true }), onSuccess: refresh });
  const voidNote = useMutation({ mutationFn: async () => api.post(`/notes/${voiding!.id}/void`, { reason }), onSuccess: () => { setVoiding(null); setReason(''); refresh(); } });
  const reinstate = useMutation({ mutationFn: async () => api.post(`/notes/${reinstating!.id}/reinstate`, { reason }), onSuccess: () => { setReinstating(null); setReason(''); refresh(); } });
  const remove = useMutation({ mutationFn: async (id: string) => api.delete(`/notes/${id}`), onSuccess: () => { setOpen(null); refresh(); } });

  const columns: Column<N>[] = [
    { key: 'number', label: 'Number', render: (n) => <span className="font-mono text-xs text-ink-600">{n.number}</span> },
    {
      key: 'noteType', label: 'Document', render: (n) => (
        <div>
          <Badge value={n.noteType === 'CREDIT' ? 'Credit note' : 'Debit note'} tone={n.noteType === 'CREDIT' ? 'neutral' : 'warning'} />
          <div className="mt-0.5 text-[11px] text-ink-400">{n.direction === 'AR' ? 'Sales' : 'Purchase'} · {String(n.reasonCode).replace(/_/g, ' ').toLowerCase()}</div>
        </div>),
    },
    { key: 'party', label: 'Against', render: (n) => <div><div className="text-ink-800">{n.customerName || n.vendorName || '—'}</div><div className="text-[11px] text-ink-400">{n.invoiceNumber ?? n.billNumber ?? 'standalone'}</div></div> },
    { key: 'issueDate', label: 'Issued', render: (n) => <DateText value={n.issueDate} />, hideOnMobile: true },
    { key: 'totalMinor', label: 'Total', align: 'right', render: (n) => <Money minor={n.totalMinor} currency={n.currency} /> },
    { key: 'remainingMinor', label: 'Unapplied', align: 'right', render: (n) => <Money minor={n.remainingMinor} currency={n.currency} className={n.remainingMinor > 0 ? 'font-medium text-ink-800' : 'text-ink-300'} /> },
    { key: 'status', label: 'Status', render: (n) => <Badge value={n.status} /> },
    {
      key: 'actions', label: '', render: (n) => (
        <div className="flex justify-end gap-1" onClick={(e) => e.stopPropagation()}>
          {n.status === 'DRAFT' && <Can do="creditDebitNotes.issue"><Button size="sm" onClick={() => issue.mutate(n.id)} disabled={issue.isPending}>Issue</Button></Can>}
          {n.noteType === 'CREDIT' && ['ISSUED', 'PART_APPLIED', 'APPLIED'].includes(n.status) && !n.reversedByNumber
            && <Can do="creditDebitNotes.issue"><Button size="sm" variant="secondary" onClick={() => setReinstating(n)}>Reinstate</Button></Can>}
          {n.status === 'ISSUED' && n.allocatedMinor === 0 && <Can do="creditDebitNotes.void"><Button size="sm" variant="ghost" onClick={() => setVoiding(n)}>Void</Button></Can>}
        </div>),
    },
  ];

  const lineTotal = lines.reduce((s, l) => s + (Number(l.unitPrice) || 0), 0);
  const region = 'UK';
  const codes: N[] = taxCodes.data?.[region] ? Object.values(taxCodes.data[region].codes) : [];
  const usingWholeDocument = form.copyWholeDocument && (form.invoiceId || form.billId);

  return (
    <div>
      <PageHeader
        title="Credit & debit notes"
        subtitle="An issued invoice is never edited — it is corrected by a further tax document. A credit note takes value off; a debit note puts value on. Both sides of the ledger, each with its own gapless number series."
        actions={<Can do="creditDebitNotes.create"><Button icon="Plus" onClick={() => setCreating(true)}>New note</Button></Can>}
      />

      {issue.isError && <p className="mb-3 rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700">{(issue.error as Error).message}</p>}

      <DataTable
        columns={columns} rows={list.rows} meta={list.meta} page={list.page} onPage={list.setPage}
        isLoading={list.isLoading} isFetching={list.isFetching} rowKey={(n) => n.id} onRowClick={setOpen}
        search={list.search} onSearch={list.setSearch} searchPlaceholder="Number, invoice, supplier, reason…"
        emptyTitle="No credit or debit notes yet"
        emptyBody="Raise one against an invoice or a supplier bill and it inherits that document's tax treatment automatically."
        filters={<>
          <Select value={String(list.filters.noteType ?? '')} onChange={(e) => list.setFilter('noteType', e.target.value)}>
            <option value="">Credit and debit</option><option value="CREDIT">Credit notes</option><option value="DEBIT">Debit notes</option>
          </Select>
          <Select value={String(list.filters.direction ?? '')} onChange={(e) => list.setFilter('direction', e.target.value)}>
            <option value="">Both ledgers</option><option value="AR">Sales (tenants)</option><option value="AP">Purchase (suppliers)</option>
          </Select>
          <Select value={String(list.filters.status ?? '')} onChange={(e) => list.setFilter('status', e.target.value)}>
            <option value="">Any status</option>{['DRAFT', 'ISSUED', 'PART_APPLIED', 'APPLIED', 'VOID'].map((s) => <option key={s}>{s}</option>)}
          </Select>
          <label className="flex items-center gap-1.5 text-xs text-ink-600">
            <input type="checkbox" checked={!!list.filters.unappliedOnly} onChange={(e) => list.setFilter('unappliedOnly', e.target.checked || undefined)} /> Unapplied only
          </label>
        </>}
      />

      {/* Create */}
      <Modal open={creating} onClose={() => setCreating(false)} title="New credit or debit note" size="lg" footer={<>
        <Button variant="secondary" onClick={() => setCreating(false)}>Cancel</Button>
        <Button onClick={() => create.mutate()} disabled={create.isPending || (!usingWholeDocument && !lines.some((l) => l.description && l.unitPrice))}>
          {create.isPending ? 'Creating…' : 'Create draft'}
        </Button>
      </>}>
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <Field label="Ledger">
              <Select value={form.direction} onChange={(e) => setForm({ ...form, direction: e.target.value, invoiceId: '', billId: '', reasonCode: REASONS[`${e.target.value}:${form.noteType}`]![0]! })} className="w-full">
                <option value="AR">Sales — a tenant or customer</option>
                <option value="AP">Purchase — a supplier</option>
              </Select>
            </Field>
            <Field label="Document">
              <Select value={form.noteType} onChange={(e) => setForm({ ...form, noteType: e.target.value, reasonCode: REASONS[`${form.direction}:${e.target.value}`]![0]! })} className="w-full">
                <option value="CREDIT">Credit note — takes value off</option>
                <option value="DEBIT">Debit note — puts value on</option>
              </Select>
            </Field>
          </div>

          <p className="rounded-xl border border-bronze-200 bg-bronze-50/60 px-3.5 py-2.5 text-[12.5px] leading-relaxed text-ink-700">{EXPLAINER[kind]}</p>

          {form.direction === 'AR' ? (
            <Field label="Invoice this relates to" hint="Optional, but a note raised from an invoice inherits its VAT codes line for line — which is the safest way to raise one.">
              <Select value={form.invoiceId} onChange={(e) => setForm({ ...form, invoiceId: e.target.value })} className="w-full">
                <option value="">— standalone —</option>
                {invoices.data?.filter((i) => i.status !== 'DRAFT').map((i) => <option key={i.id} value={i.id}>{i.number} · {i.customerName ?? '—'} · {(i.totalMinor / 100).toFixed(2)} {i.currency}</option>)}
              </Select>
            </Field>
          ) : (
            <Field label="Supplier bill this relates to">
              <Select value={form.billId} onChange={(e) => setForm({ ...form, billId: e.target.value })} className="w-full">
                <option value="">— standalone —</option>
                {bills.data?.filter((b) => b.status !== 'DRAFT').map((b) => <option key={b.id} value={b.id}>{b.number} · {b.vendorName ?? '—'} · {(b.totalMinor / 100).toFixed(2)} {b.currency}</option>)}
              </Select>
            </Field>
          )}

          <div className="grid grid-cols-2 gap-3">
            <Field label="Reason code">
              <Select value={form.reasonCode} onChange={(e) => setForm({ ...form, reasonCode: e.target.value })} className="w-full">
                {(REASONS[kind] ?? ['OTHER']).map((c) => <option key={c} value={c}>{c.replace(/_/g, ' ').toLowerCase().replace(/^\w/, (x) => x.toUpperCase())}</option>)}
              </Select>
            </Field>
            <Field label="Issue date"><Input type="date" value={form.issueDate} onChange={(e) => setForm({ ...form, issueDate: e.target.value })} className="w-full" /></Field>
          </div>

          <Field label="Reason" hint="Shown on the document and kept in the audit trail."><Input value={form.reason} onChange={(e) => setForm({ ...form, reason: e.target.value })} placeholder="One week rent remitted — no hot water" className="w-full" /></Field>

          {(form.invoiceId || form.billId) && (
            <label className="flex items-start gap-2 rounded-xl border border-ink-200 bg-ink-50/60 px-3.5 py-3 text-[12.5px] text-ink-700">
              <input type="checkbox" className="mt-0.5" checked={form.copyWholeDocument} onChange={(e) => setForm({ ...form, copyWholeDocument: e.target.checked })} />
              <span>
                <strong className="font-medium text-ink-900">{form.noteType === 'CREDIT' ? 'Credit' : 'Debit'} the whole document</strong>
                <span className="block text-ink-500">Copies every line at its original tax code. Untick to enter your own lines for a partial adjustment.</span>
              </span>
            </label>
          )}

          {!usingWholeDocument && (
            <div>
              <div className="mb-1 flex items-center justify-between">
                <span className="text-xs font-medium text-ink-600">Lines</span>
                <button onClick={() => setLines([...lines, blankLine()])} className="inline-flex items-center gap-1 text-xs text-bronze-600 hover:underline"><Plus className="h-3.5 w-3.5" /> Add line</button>
              </div>
              <div className="space-y-2">
                {lines.map((l, i) => (
                  <div key={i} className="grid grid-cols-[1fr_120px_110px_28px] items-center gap-2">
                    <Input value={l.description} onChange={(e) => setLines(lines.map((x, k) => (k === i ? { ...x, description: e.target.value } : x)))} placeholder="Description" />
                    <Select value={l.taxCode} onChange={(e) => setLines(lines.map((x, k) => (k === i ? { ...x, taxCode: e.target.value } : x)))}>
                      <option value="">Default VAT</option>
                      {codes.map((c) => <option key={c.code} value={c.code}>{c.code} · {c.ratePercent}%</option>)}
                    </Select>
                    <Input type="number" step="0.01" value={l.unitPrice} onChange={(e) => setLines(lines.map((x, k) => (k === i ? { ...x, unitPrice: e.target.value } : x)))} placeholder="Amount" />
                    <button onClick={() => setLines(lines.length > 1 ? lines.filter((_, k) => k !== i) : [blankLine()])} className="rounded p-1 text-ink-300 hover:text-red-500"><Trash2 className="h-4 w-4" /></button>
                  </div>
                ))}
              </div>
              <p className="mt-2 text-right text-xs text-ink-400">Net {lineTotal.toFixed(2)} — VAT is added from the code on each line</p>
            </div>
          )}

          <Field label="Internal notes"><Textarea rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="w-full" /></Field>
          {create.isError && <p className="text-xs text-red-600">{(create.error as Error).message}</p>}
        </div>
      </Modal>

      {/* Detail */}
      <Modal open={!!open} onClose={() => setOpen(null)} title={detail.data?.number ?? open?.number ?? 'Note'} size="lg" footer={detail.data && <>
        {detail.data.status === 'DRAFT' && <Can do="creditDebitNotes.delete"><Button variant="ghost" onClick={() => remove.mutate(detail.data!.id)}>Delete draft</Button></Can>}
        {detail.data.status === 'DRAFT' && <Can do="creditDebitNotes.issue"><Button onClick={() => issue.mutate(detail.data!.id)} disabled={issue.isPending}>{issue.isPending ? 'Issuing…' : 'Issue note'}</Button></Can>}
        {detail.data.noteType === 'CREDIT' && ['ISSUED', 'PART_APPLIED', 'APPLIED'].includes(detail.data.status) && !detail.data.reversedByNumber
          && <Can do="creditDebitNotes.issue"><Button variant="secondary" onClick={() => setReinstating(detail.data!)}>Reinstate with a debit note</Button></Can>}
      </>}>
        {detail.data && (
          <div className="space-y-4">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <div className="flex items-center gap-2">
                <Badge value={detail.data.status} />
                <Badge value={detail.data.noteType === 'CREDIT' ? 'Credit note' : 'Debit note'} tone={detail.data.noteType === 'CREDIT' ? 'neutral' : 'warning'} />
              </div>
              <span className="text-xs text-ink-400">{detail.data.entityName} · {detail.data.direction === 'AR' ? 'sales ledger' : 'purchase ledger'}</span>
            </div>

            <p className="rounded-xl bg-ink-50 px-3.5 py-2.5 text-[12.5px] text-ink-700">{detail.data.effect}</p>

            <KeyValue items={[
              { label: 'Against', value: detail.data.invoiceNumber ?? detail.data.billNumber ?? 'Standalone' },
              { label: detail.data.direction === 'AR' ? 'Customer' : 'Supplier', value: detail.data.customerName || detail.data.vendorName || '—' },
              { label: 'Issued', value: <DateText value={detail.data.issueDate} /> },
              { label: 'Reason', value: `${String(detail.data.reasonCode).replace(/_/g, ' ').toLowerCase()}${detail.data.reason ? ` — ${detail.data.reason}` : ''}` },
            ]} />

            {detail.data.reversedByNumber && (
              <p className="rounded-xl border border-amber-200 bg-amber-50 px-3.5 py-2.5 text-[12.5px] text-amber-900">
                Reinstated by debit note <strong>{detail.data.reversedByNumber}</strong>. Both documents stay on file — an issued tax document is never deleted.
              </p>
            )}

            <table className="w-full text-sm">
              <thead><tr className="border-b border-ink-100 text-left text-[11px] uppercase text-ink-400"><th className="py-1.5">Line</th><th className="py-1.5 text-right">Net</th><th className="py-1.5 text-right">VAT</th></tr></thead>
              <tbody>
                {detail.data.lines?.map((l: N) => (
                  <tr key={l.id} className="border-b border-ink-50">
                    <td className="py-1.5 text-ink-700">{l.description}</td>
                    <td className="py-1.5 text-right"><Money minor={l.netMinor} currency={detail.data!.currency} /></td>
                    <td className="py-1.5 text-right text-ink-500"><Money minor={l.taxMinor} currency={detail.data!.currency} /><span className="ml-1 text-[10px]">{l.taxCode} {l.taxRate}%</span></td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr><td className="pt-2 text-right text-ink-500" colSpan={2}>Subtotal</td><td className="pt-2 text-right"><Money minor={detail.data.subtotalMinor} currency={detail.data.currency} /></td></tr>
                <tr><td className="text-right text-ink-500" colSpan={2}>VAT</td><td className="text-right"><Money minor={detail.data.taxMinor} currency={detail.data.currency} /></td></tr>
                <tr className="font-semibold"><td className="text-right" colSpan={2}>Total</td><td className="text-right"><Money minor={detail.data.totalMinor} currency={detail.data.currency} /></td></tr>
                <tr><td className="text-right text-ink-500" colSpan={2}>Applied</td><td className="text-right"><Money minor={detail.data.allocatedMinor} currency={detail.data.currency} /></td></tr>
                <tr className="font-medium"><td className="text-right" colSpan={2}>Unapplied</td><td className="text-right"><Money minor={detail.data.remainingMinor} currency={detail.data.currency} /></td></tr>
              </tfoot>
            </table>

            {!!detail.data.allocations?.length && (
              <Card title="How it has been applied">
                <ul className="space-y-1.5 text-[12.5px] text-ink-700">
                  {detail.data.allocations.map((a: N) => (
                    <li key={a.id} className="flex items-center justify-between">
                      <span>{a.sourceReference} → {a.targetReference} <span className="text-ink-400">({a.targetType.toLowerCase()})</span></span>
                      <span className="flex items-center gap-3"><span className="text-ink-400"><DateText value={a.allocatedOn} /></span><Money minor={a.amountMinor} currency={a.currency} /></span>
                    </li>
                  ))}
                </ul>
              </Card>
            )}

            {detail.data.journalId && <p className="text-[11px] text-ink-400">Posted to the ledger — see Journals for the entry.</p>}
            {detail.data.notes && <p className="whitespace-pre-wrap text-xs text-ink-500">{detail.data.notes}</p>}
          </div>
        )}
      </Modal>

      {/* Void */}
      <Modal open={!!voiding} onClose={() => { setVoiding(null); setReason(''); }} title={`Void ${voiding?.number ?? ''}`} footer={<>
        <Button variant="secondary" onClick={() => { setVoiding(null); setReason(''); }}>Cancel</Button>
        <Button variant="danger" onClick={() => voidNote.mutate()} disabled={!reason || voidNote.isPending}>Void note</Button>
      </>}>
        <p className="mb-3 text-[13px] text-ink-600">Reverses the journal. Only possible while nothing has been applied — once value has moved, raise the opposite note instead.</p>
        <Field label="Reason"><Input value={reason} onChange={(e) => setReason(e.target.value)} className="w-full" /></Field>
        {voidNote.isError && <p className="mt-2 text-xs text-red-600">{(voidNote.error as Error).message}</p>}
      </Modal>

      {/* Reinstate */}
      <Modal open={!!reinstating} onClose={() => { setReinstating(null); setReason(''); }} title={`Reinstate ${reinstating?.number ?? ''}`} footer={<>
        <Button variant="secondary" onClick={() => { setReinstating(null); setReason(''); }}>Cancel</Button>
        <Button onClick={() => reinstate.mutate()} disabled={!reason || reinstate.isPending}>Raise debit note</Button>
      </>}>
        <p className="mb-3 text-[13px] leading-relaxed text-ink-600">
          An issued credit note cannot be deleted — it is a tax document. Raising a debit note of the same value puts the debt back, and the pair is linked so an auditor can see what happened.
        </p>
        <Field label="Why is it being reinstated?"><Input value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Credit raised against the wrong tenancy" className="w-full" /></Field>
        {reinstate.isError && <p className="mt-2 text-xs text-red-600">{(reinstate.error as Error).message}</p>}
      </Modal>
    </div>
  );
}
